import { currentSubscriptions } from "@features/account/memberships"
import { useAccount } from "@features/account/useAccount"
import { flyingCarpetStore } from "@features/flying-carpet/flying-carpet"
import { QuantitySelector } from "@features/atoms/QuantitySelector"
import { Button } from "@features/atoms/Button"
import { formatDollars } from "@utils/format-dollars"
import { useRPCQuery } from "eddev/hooks"
import { CheckoutStepLayout } from "./CheckoutStepLayout"
import { useCheckout } from "./useCheckout"
import { useMemo, useState } from "react"
import {
  eventTickets,
  existingTicketSelection,
  selectableTicketPrices,
  selectionSummary,
  TicketSelection,
} from "./ticket-selection"
import { ResponsiveImage } from "@features/atoms/ResponsiveImage"
import { ItemGroup } from "@features/atoms/ItemGroup"
import { Chip } from "@features/atoms/Chip"
import { formatDate } from "@utils/dates"

export function SelectTicketsCheckoutStep() {
  const checkout = useCheckout()
  const req = checkout.ticketSelectionRequest!
  const event = req.event!

  const sessionId = req?.selectedSession?.sessionId
  const loader = useRPCQuery("api.ticketing.ticketPricing", { sessionId: sessionId ?? 0 }, { enabled: !!sessionId })
  const config = loader.data
  const prices = useMemo(() => (config ? selectableTicketPrices(config) : []), [config])

  // Use the cached basket until the user starts editing their selection.
  const [draft, setDraft] = useState<TicketSelection | null>(null)
  const existing = config ? eventTickets(config, checkout.basket?.tickets ?? []) : []
  const selection = draft ?? (config ? existingTicketSelection(config, existing) : {})
  const heldTickets = existing.filter((ticket) => ticket.instance.id === config?.instanceId).length
  const maxTickets = config ? (checkout.instanceLimits[config.instanceId] ?? null) : null

  const summary = useMemo(() => {
    return config
      ? selectionSummary(config, selection, heldTickets, maxTickets, existing.length > 0)
      : { totalPrice: 0, totalQuantity: 0, isValidSelection: false, validityWarning: null, items: [] }
  }, [selection, config, heldTickets, maxTickets, existing.length])

  async function next() {
    if (!loader.isSuccess || !config || !checkout.basket || !summary.isValidSelection) return
    if (await checkout.replaceTickets(config, selection)) checkout.goToStep("payment")
  }

  return (
    <CheckoutStepLayout
      header="Select tickets"
      children={
        <div className="pt-7 type-body-s text-blue">
          <MemberPrompt />
          <h4 className="type-label-s mb-4">Tickets</h4>
          {!sessionId ? (
            <p className="type-body-xs">Choose a session to select tickets.</p>
          ) : loader.isPending ? (
            <p className="type-body-xs" role="status">
              Loading ticket prices…
            </p>
          ) : loader.isError ? (
            <p className="type-body-xs" role="alert">
              Ticket prices are unavailable.
            </p>
          ) : !checkout.basket ? (
            <p className="type-body-xs" role="status">
              {checkout.isPending ? "Loading your cart…" : "Your cart could not be loaded. Please reload to try again."}
            </p>
          ) : prices.length ? (
            <>
              <table className="w-full table-fixed border-collapse text-left" aria-label="Ticket prices">
                <colgroup>
                  <col />
                  <col className="w-[88px] md:w-[112px]" />
                  <col className="w-[80px] md:w-[104px]" />
                </colgroup>
                <thead className="sr-only">
                  <tr>
                    <th scope="col">Ticket type</th>
                    <th scope="col">Price</th>
                    <th scope="col">Quantity</th>
                  </tr>
                </thead>
                <tbody className="border-t border-blue">
                  {prices.map((price) => (
                    <tr key={price.id} className="border-b border-blue">
                      <th scope="row" className="py-3 pr-4 align-top font-normal break-words">
                        <span className="block">{price.ticketType.name}</span>
                      </th>
                      <td className="py-3 align-top">
                        <span className="type-label-s whitespace-nowrap tabular-nums">
                          {formatDollars(price.amount)}
                        </span>
                        {price.customItems
                          .filter((item) => item.displayMode !== "IncludedInPrice")
                          .map((item, index) => (
                            <span key={index} className="block mt-1 type-body-xxs text-blue-light">
                              {item.displayMode === "SeparateFromPrice" ? "+ " : "Includes "}
                              {formatDollars(item.amount)} {item.name}
                            </span>
                          ))}
                      </td>
                      <td className="py-3 align-top">
                        <QuantitySelector
                          label={`${price.ticketType.name.trim()} quantity`}
                          value={selection[price.id] ?? 0}
                          max={Math.max(
                            0,
                            config!.status.available + heldTickets - summary.totalQuantity + (selection[price.id] ?? 0),
                          )}
                          disabled={checkout.isPending || !config!.instance.isOnSale || config!.instance.cancelled}
                          onChange={(value) => {
                            setDraft({ ...selection, [price.id]: value })
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p className="type-body-xs">No ticket prices are listed for this session.</p>
          )}
          {summary.validityWarning && (
            <p className="type-body-xs text-red mt-4" role="alert">
              {summary.validityWarning}
            </p>
          )}
        </div>
      }
      footer={
        <Button
          variant="filled"
          className="disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={!loader.isSuccess || !checkout.basket || !summary.isValidSelection || checkout.isPending}
          onClick={next}
          loading={checkout.isPending}
        >
          Continue
        </Button>
      }
      sidePanel={
        <div>
          {/* Event summary */}
          {event && (
            <div className="flex flex-row gap-3">
              <ResponsiveImage aspect={16 / 9} {...event.filmData?.featuredImage} className="flex-none w-1/3" />
              <div className="flex flex-col gap-1 flex-1">
                <div className="type-title-xs">{event.title}</div>
                {event.filmData?.durationMinutes && (
                  <div className="type-label-s">{event.filmData?.durationMinutes + " mins"}</div>
                )}
              </div>
            </div>
          )}
          <div className="h-4" />
          {!!loader.data?.instance.startUtc && (
            <ItemGroup label="Session">
              <Chip variant="invert" rounded>
                {formatDate(loader.data?.instance.startUtc, "DATE_AND_TIME")}
              </Chip>
            </ItemGroup>
          )}
          {summary.items.length > 0 && (
            <ItemGroup label="Session">
              <div className="gap-1 flex">
                {summary.items.map((item, i) => (
                  <Chip key={i} variant="invert" rounded>
                    {item.label} ({item.quantity})
                  </Chip>
                ))}
              </div>
            </ItemGroup>
          )}
        </div>
      }
    />
  )
}

/** Invites guests and signed in customers without a current membership to join. */
function MemberPrompt() {
  const account = useAccount()
  const checkout = useCheckout()
  // Wait for the session check, so members never see the invitation flash up.
  if (!account.isLoaded) return null
  const customer = account.customer
  // Hidden until a signed in customer's memberships are known, and for any current membership.
  if (customer && (!customer.subscriptions || currentSubscriptions(customer.subscriptions).length)) return null

  return (
    <section className="mb-9">
      <p>Are you a member? Priority booking, free screenings, and 20% off concessions year-round.</p>
      <div className="mt-5 flex flex-wrap gap-4">
        {!customer && (
          <Button
            variant="filled"
            className="px-5"
            onClick={() => {
              checkout.close()
              flyingCarpetStore.open("account/login")
            }}
          >
            Sign in
          </Button>
        )}
        <Button variant="outline" className="px-5" href="/memberships" onClick={() => checkout.close()}>
          Become a member
        </Button>
      </div>
    </section>
  )
}
